import { BULK_RUN_RESOURCE, type BulkRunResource } from '@studio/shared';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.js';
import { useResourceEvents } from '../stream.js';

/** A run is finished when it has nothing left to do, whichever way it got there. */
function unfinished(run: BulkRunResource): boolean {
  return run.state !== 'completed' && run.state !== 'stopped';
}

/**
 * The Bulk Runs that are not over, for the indicator that is on every screen.
 *
 * R177 names run-level Paused first, and a paused run has no Job in flight by
 * definition - neither has one waiting on an allowance. Both are work the operator has
 * to come back to, and neither is visible anywhere except the Bulk Runs surface, which
 * is the one place R177 says the detail must not be confined to. So the shell knows
 * which runs are outstanding, and the indicator is what shows them.
 *
 * It is a read on the way in and a read on every `bulkRun` event after that: the shell
 * already has one stream, and a run changes state rarely enough that a poll would be
 * the wrong shape entirely.
 */
export function useUnfinishedRuns(live: boolean): readonly BulkRunResource[] {
  const [runs, setRuns] = useState<readonly BulkRunResource[]>([]);

  const reload = useCallback(() => {
    if (!live) {
      setRuns([]);
      return;
    }
    void api
      .listBulkRuns()
      .then((answer) => setRuns(answer.runs.filter(unfinished)))
      // Not knowing is not the same as knowing there are none, but an indicator that
      // guesses is worse than one that waits for the next event.
      .catch(() => setRuns([]));
  }, [live]);

  useEffect(reload, [reload]);
  useResourceEvents(
    useCallback(
      (event: { resource: string }) => {
        if (event.resource === BULK_RUN_RESOURCE) reload();
      },
      [reload],
    ),
  );

  return runs;
}
